/**
 * 服务配置文件
 * 统一管理所有后端服务的 URL 配置
 *
 * 【运维人员使用说明】
 *
 * 1. 环境切换：修改 ENV_CONFIG 中的开关
 *    - IS_PRODUCTION: true=生产环境, false=开发环境
 *    - USE_HTTPS: true=HTTPS/WSS, false=HTTP/WS
 *    - USE_NGINX_PROXY: true=通过Nginx反代, false=直接端口访问
 *    - AGENT_USE_NGINX: true=智能体iframe通过Nginx, false=直接IP访问
 *
 * 2. 常见场景配置：
 *    - 开发环境：IS_PRODUCTION=false, USE_HTTPS=false, USE_NGINX_PROXY=false
 *    - 测试服务器(直连IP)：IS_PRODUCTION=true, USE_HTTPS=false, USE_NGINX_PROXY=false
 *      → 所有服务地址自动用 window.location.hostname，无需改 IP
 *    - 生产环境(HTTP+Nginx)：IS_PRODUCTION=true, USE_HTTPS=false, USE_NGINX_PROXY=true
 *    - 生产环境(HTTPS+Nginx)：IS_PRODUCTION=true, USE_HTTPS=true, USE_NGINX_PROXY=true
 *
 * 3. 部署时只需修改此文件中的 ENV_CONFIG 部分
 */

// ========== 环境与协议开关 ==========
export const ENV_CONFIG = {
  IS_PRODUCTION: true,                 // 【主开关】true=生产环境, false=开发环境
  USE_HTTPS: true,                         // 【协议开关】true=HTTPS/WSS, false=HTTP/WS
  USE_NGINX_PROXY: true,           // 【反代开关】true=通过Nginx反代, false=直接端口访问
  // 生产 + HTTPS 时务必开启：否则 iframe 会用 IP/HTTP，导致"混合内容/证书不匹配"，浏览器一直显示不安全
  AGENT_USE_NGINX: false,      // 【Dify开关】智能体iframe是否通过Nginx反代
};

// ========== 功能开关 ==========
export const FEATURE_FLAGS = {
  GUEST_LOGIN_ENABLED: false,               // 【访客开关】true=开放访客注册/登录, false=关闭（显示商务合作）
  BUSINESS_CONTACT_EMAIL: '1848439579@qq.com', // 商务合作邮箱
};

// ========== 基础域名/IP配置 ==========
const DOMAIN_CONFIG = {
  PRODUCTION_DOMAIN: 'baigongai.cn',   // 生产域名
  PRODUCTION_IP: '49.232.89.199',      // 生产IP（Nginx未启用时使用）
  DEV_HOST: 'localhost',               // 开发环境主机
};

// ========== 开发环境端口配置（本机直接运行，无 Docker 端口映射）==========
const DEV_PORTS = {
  EDUGATE: 7861,           // EduGate主API
  GOMAOWN: 8000,           // GoMAown训练后端（本机直接运行）
  CCMA: 8001,              // CCMA先进综合训练后端（本机直接运行）
  AI_SECRETARY: 9421,      // AI秘书
  VISITOR_AUTH: 5013,      // 访客认证
  GVA_STATIC: 8888,        // gin-vue-admin静态服务
  GVA_FRONTEND: 8080,      // gin-vue-admin前端
  AIDRAWIO: 6001,          // AI绘图
  EXPERT_SERVICE: 8090,    // ExpertService 数字人+专家服务
  DISCUSS_HUB: 8010,       // DiscussHub 研讨厅
  GROUP_CHAT_HUB: 8020,    // GroupChatHub 群聊协作
};

// ========== 生产/测试服务器宿主机端口（Docker 端口映射后对外暴露的端口）==========
// 与 DEV_PORTS 不同的项才需要列出
const PROD_PORTS = {
  ...DEV_PORTS,
  GOMAOWN: 9999,           // GoMAown: 容器内8000，宿主机映射为9999
  CCMA: 8001,              // CCMA: 容器内8001，宿主机同端口（按需修改）
  EXPERT_SERVICE: 8090,    // 专家数字人 ExpertService（文字对话，测试/生产直连端口）
  DIGITAL_HUMAN: 6086,     // 音视频数字人（TTS/渲染，仅测试服务器 43.143.244.25 提供）
};

// ========== 生产环境Nginx路径配置 ==========
const NGINX_PATHS = {
  API: '/edugate',             // EduGate主API（避免与 Dify 的 /api 冲突）
  WS: '/ws',                   // GoMAown训练WebSocket
  CCMA_WS: '/ws/ccma',         // CCMA先进综合训练WebSocket
  CCMA_API: '/api/ccma',       // CCMA HTTP API（Nginx 前缀，转发后还原为 /api/*）
  TRAIN: '/train',             // GoMAown训练API
  SECRETARY: '/secretary/ws',  // AI秘书 WebSocket（同域反代）
  VISITOR: '/visitor',         // 访客认证
  STATIC: '/static',           // 静态文件服务
  ADMIN: '/admin',             // 管理后台前端
  ASR: '/asr',                 // FunASR语音识别（Nginx代理WSS）
  EXPERT: '/expert-service',   // 专家数字人 ExpertService（Nginx: /expert-service/ -> expert-service:8090）
  DIGITAL_HUMAN: '/expert',    // 音视频数字人（Nginx: /expert/ -> 43.143.244.25:6086）
  GVA_API: '/gva-api',         // 管理后台API（Nginx反代）
  AIDRAWIO: '/aidrawio',       // AI绘图服务（Nginx反代）
  DISCUSS: '/discuss',         // 研讨厅WebSocket（Nginx反代）
  GROUP_CHAT: '/groupchat',    // 群聊协作WebSocket（Nginx反代）
};

// ========== 其他服务器配置 ==========
// 说明：
// - 开发环境：使用 DEV_SERVERS 中的直连地址
// - 生产环境 + HTTPS：任何硬编码的 http:// / ws:// 都会触发浏览器"混合内容"警告
//   所以生产环境统一走同域 Nginx 反代（/expert-service、/expert、/asr、/gva-api）

// 专家数字人（ExpertService，文字对话）
const EXPERT_SERVICE_DEV = 'http://localhost:8090';      // 本地开发：连本地 ExpertService

// 音视频数字人（TTS/渲染，本地无此服务，固定指向测试服务器）
const DIGITAL_HUMAN_DEV = 'http://43.143.244.25:6086';

const DEV_SERVERS = {
  EXPERT_SERVICE_LOCAL: 'http://localhost:8090',   // 本地 ExpertService（需本地运行，连本地 ybg-mysql）
  EXPERT_SERVICE_REMOTE: 'http://43.143.244.25:6086',  // 远程数字人+TTS服务
  FUNASR_DIRECT: 'ws://43.143.244.25:10095',       // FunASR直连地址
  GVA_API: 'http://localhost:8888',              // 管理后台API（开发环境直连）
};

// ========== 协议计算辅助函数 ==========
const getHttpProtocol = () => ENV_CONFIG.USE_HTTPS ? 'https' : 'http';
const getWsProtocol = () => ENV_CONFIG.USE_HTTPS ? 'wss' : 'ws';

// ========== 主机地址计算 ==========
const getHost = () => {
  if (!ENV_CONFIG.IS_PRODUCTION) {
    return DOMAIN_CONFIG.DEV_HOST;
  }
  if (ENV_CONFIG.USE_NGINX_PROXY) {
    return DOMAIN_CONFIG.PRODUCTION_DOMAIN;
  }
  // 直连模式：用浏览器当前访问的 hostname，自动适配测试服务器和生产服务器
  return typeof window !== 'undefined' ? window.location.hostname : DOMAIN_CONFIG.PRODUCTION_IP;
};

// ========== URL生成函数 ==========

/**
 * 生成EduGate API基础URL
 * 开发环境：http://localhost:7861
 * 生产环境(Nginx)：http(s)://baigongai.cn/edugate
 * 生产环境(直连)：http(s)://49.232.89.199:7861
 */
const buildEdugateApiUrl = () => {
  const protocol = getHttpProtocol();
  const host = getHost();

  if (!ENV_CONFIG.IS_PRODUCTION) {
    return `${protocol}://${host}:${DEV_PORTS.EDUGATE}`;
  }

  if (ENV_CONFIG.USE_NGINX_PROXY) {
    return `${protocol}://${host}${NGINX_PATHS.API}`;
  }

  return `${protocol}://${host}:${DEV_PORTS.EDUGATE}`;
};

/**
 * 生成GoMAown训练后端API URL
 */
const buildGomaownApiUrl = () => {
  const protocol = getHttpProtocol();
  const host = getHost();

  if (!ENV_CONFIG.IS_PRODUCTION) {
    return `${protocol}://${host}:${DEV_PORTS.GOMAOWN}`;
  }

  if (ENV_CONFIG.USE_NGINX_PROXY) {
    return `${protocol}://${host}${NGINX_PATHS.TRAIN}`;
  }

  // 直连模式：用宿主机映射端口 9999（容器内 8000 → 宿主机 9999）
  return `${protocol}://${host}:${PROD_PORTS.GOMAOWN}`;
};

/**
 * 生成GoMAown训练WebSocket URL
 */
const buildGomaownWsUrl = () => {
  const protocol = getWsProtocol();
  const host = getHost();

  if (!ENV_CONFIG.IS_PRODUCTION) {
    return `${protocol}://${host}:${DEV_PORTS.GOMAOWN}/ws`;
  }

  if (ENV_CONFIG.USE_NGINX_PROXY) {
    return `${protocol}://${host}${NGINX_PATHS.WS}`;
  }

  // 直连模式：用宿主机映射端口 9999（容器内 8000 → 宿主机 9999）
  return `${protocol}://${host}:${PROD_PORTS.GOMAOWN}/ws`;
};

/**
 * 生成CCMA先进综合训练WebSocket URL
 * 开发环境：ws://localhost:8001/ws/ccma
 * 生产环境(Nginx)：wss://{domain}/ws/ccma
 * 生产环境(直连)：ws://{hostname}:8001/ws/ccma
 */
const buildCcmaWsUrl = () => {
  const protocol = getWsProtocol();
  const host = getHost();

  if (!ENV_CONFIG.IS_PRODUCTION) {
    return `${protocol}://${host}:${DEV_PORTS.CCMA}/ws/ccma`;
  }

  if (ENV_CONFIG.USE_NGINX_PROXY) {
    return `${protocol}://${host}${NGINX_PATHS.CCMA_WS}`;
  }

  return `${protocol}://${host}:${PROD_PORTS.CCMA}/ws/ccma`;
};

/**
 * 生成AI秘书WebSocket URL
 */
const buildAiSecretaryWsUrl = () => {
  const protocol = getWsProtocol();
  const host = getHost();

  if (!ENV_CONFIG.IS_PRODUCTION) {
    return `${protocol}://${host}:${DEV_PORTS.AI_SECRETARY}/ws`;
  }

  if (ENV_CONFIG.USE_NGINX_PROXY) {
    return `${protocol}://${host}${NGINX_PATHS.SECRETARY}`;
  }

  return `${protocol}://${host}:${DEV_PORTS.AI_SECRETARY}/ws`;
};

/**
 * 生成访客认证服务URL
 */
const buildVisitorAuthUrl = () => {
  const protocol = getHttpProtocol();
  const host = getHost();

  if (!ENV_CONFIG.IS_PRODUCTION) {
    return `${protocol}://${host}:${DEV_PORTS.VISITOR_AUTH}`;
  }

  if (ENV_CONFIG.USE_NGINX_PROXY) {
    return `${protocol}://${host}${NGINX_PATHS.VISITOR}`;
  }

  return `${protocol}://${host}:${DEV_PORTS.VISITOR_AUTH}`;
};

/**
 * 生成静态文件服务URL
 */
const buildGvaStaticUrl = () => {
  const protocol = getHttpProtocol();
  const host = getHost();

  if (!ENV_CONFIG.IS_PRODUCTION) {
    return `${protocol}://${host}:${DEV_PORTS.GVA_STATIC}`;
  }

  if (ENV_CONFIG.USE_NGINX_PROXY) {
    return `${protocol}://${host}${NGINX_PATHS.STATIC}`;
  }

  return `${protocol}://${host}:${DEV_PORTS.GVA_STATIC}`;
};

/**
 * 生成管理后台前端URL
 * 开发环境：http://localhost:8080
 * 生产环境(Nginx+域名)：https://gva.baigongai.cn（独立子域名）
 * 生产环境(直连)：http://{hostname}:8887
 */
const buildGvaFrontendUrl = () => {
  const protocol = getHttpProtocol();
  const host = getHost();

  if (!ENV_CONFIG.IS_PRODUCTION) {
    return `${protocol}://${host}:${DEV_PORTS.GVA_FRONTEND}`;
  }

  // 生产环境 + Nginx + 域名：走独立子域名
  if (ENV_CONFIG.USE_NGINX_PROXY) {
    return `${protocol}://gva.${DOMAIN_CONFIG.PRODUCTION_DOMAIN}`;
  }

  // 直连模式（测试服务器等）：走当前 hostname + gva-web 端口
  return `${protocol}://${host}:8887`;
};

/**
 * 生成AI绘图服务URL
 */
const buildAidrawioUrl = () => {
  const protocol = getHttpProtocol();
  const host = getHost();

  if (!ENV_CONFIG.IS_PRODUCTION) {
    return `${protocol}://${host}:${DEV_PORTS.AIDRAWIO}`;
  }

  if (ENV_CONFIG.USE_NGINX_PROXY) {
    return `${protocol}://${host}${NGINX_PATHS.AIDRAWIO}`;
  }

  return `${protocol}://${host}:${DEV_PORTS.AIDRAWIO}`;
};

/**
 * 生成DiscussHub研讨厅WebSocket URL
 */
const buildDiscussHubWsUrl = () => {
  const protocol = getWsProtocol();
  const host = getHost();

  if (!ENV_CONFIG.IS_PRODUCTION) {
    return `${protocol}://${host}:${DEV_PORTS.DISCUSS_HUB}/ws/discuss`;
  }

  if (ENV_CONFIG.USE_NGINX_PROXY) {
    return `${protocol}://${host}${NGINX_PATHS.DISCUSS}`;
  }

  return `${protocol}://${host}:${DEV_PORTS.DISCUSS_HUB}/ws/discuss`;
};

/**
 * 生成GroupChatHub群聊协作WebSocket URL
 */
const buildGroupChatHubWsUrl = () => {
  const protocol = getWsProtocol();
  const host = getHost();

  if (!ENV_CONFIG.IS_PRODUCTION) {
    return `${protocol}://${host}:${DEV_PORTS.GROUP_CHAT_HUB}/ws/groupchat`;
  }

  if (ENV_CONFIG.USE_NGINX_PROXY) {
    return `${protocol}://${host}${NGINX_PATHS.GROUP_CHAT}`;
  }

  return `${protocol}://${host}:${DEV_PORTS.GROUP_CHAT_HUB}/ws/groupchat`;
};

/**
 * 生成GroupChatHub群聊协作HTTP API URL
 */
const buildGroupChatHubHttpUrl = () => {
  const protocol = getHttpProtocol();
  const host = getHost();

  if (!ENV_CONFIG.IS_PRODUCTION) {
    return `${protocol}://${host}:${DEV_PORTS.GROUP_CHAT_HUB}`;
  }

  if (ENV_CONFIG.USE_NGINX_PROXY) {
    return `${protocol}://${host}${NGINX_PATHS.GROUP_CHAT}`;
  }

  return `${protocol}://${host}:${DEV_PORTS.GROUP_CHAT_HUB}`;
};

/**
 * 生成DiscussHub研讨厅HTTP API URL
 */
const buildDiscussHubHttpUrl = () => {
  const protocol = getHttpProtocol();
  const host = getHost();

  if (!ENV_CONFIG.IS_PRODUCTION) {
    return `${protocol}://${host}:${DEV_PORTS.DISCUSS_HUB}`;
  }

  if (ENV_CONFIG.USE_NGINX_PROXY) {
    // HTTP API 与 WebSocket 共用同一路径前缀，但去掉 /ws/discuss
    return `${protocol}://${host}${NGINX_PATHS.DISCUSS.replace('/ws/discuss', '')}`;
  }

  return `${protocol}://${host}:${DEV_PORTS.DISCUSS_HUB}`;
};

/**
 * 生成Dify智能体基础URL
 * 开发环境：http://49.232.89.199（直连生产Dify）
 * 生产环境(Nginx+域名)：https://dify.baigongai.cn（独立子域名）
 * 生产环境(直连)：http://{hostname}（Dify 跑在同一台服务器的 80 端口）
 */
const buildDifyBaseUrl = () => {
  const protocol = getHttpProtocol();

  if (!ENV_CONFIG.IS_PRODUCTION) {
    // 开发环境直接访问生产Dify
    return `${protocol}://${DOMAIN_CONFIG.PRODUCTION_IP}`;
  }

  if (ENV_CONFIG.AGENT_USE_NGINX) {
    // 生产环境：Dify 走独立子域名
    return `${protocol}://dify.${DOMAIN_CONFIG.PRODUCTION_DOMAIN}`;
  }

  // 直连模式：Dify 跑在当前服务器的 80 端口
  const host = getHost();
  return `${protocol}://${host}`;
};

/**
 * 生成专家数字人服务URL（ExpertService，文字对话）
 * 开发环境：http://localhost:8090
 * 测试服务器（直连）：http://{hostname}:8090
 * 生产环境（Nginx）：https://baigongai.cn/expert-service  -> expert-service:8090
 */
const buildExpertServiceUrl = () => {
  const protocol = getHttpProtocol();
  const host = getHost();

  if (!ENV_CONFIG.IS_PRODUCTION) {
    return EXPERT_SERVICE_DEV;
  }

  if (ENV_CONFIG.USE_NGINX_PROXY) {
    return `${protocol}://${host}${NGINX_PATHS.EXPERT}`;
  }

  // 直连模式（测试服务器）：{hostname}:8090
  return `${protocol}://${host}:${PROD_PORTS.EXPERT_SERVICE}`;
};

/**
 * 生成音视频数字人服务URL（TTS/渲染，仅测试/生产服务器有）
 * 开发环境：http://43.143.244.25:6086  （固定远程，本地无此服务）
 * 测试服务器（直连）：http://{hostname}:6086
 * 生产环境（Nginx）：https://baigongai.cn/expert  -> 43.143.244.25:6086
 */
const buildDigitalHumanUrl = () => {
  const protocol = getHttpProtocol();
  const host = getHost();

  if (!ENV_CONFIG.IS_PRODUCTION) {
    return DIGITAL_HUMAN_DEV;
  }

  if (ENV_CONFIG.USE_NGINX_PROXY) {
    return `${protocol}://${host}${NGINX_PATHS.DIGITAL_HUMAN}`;
  }

  // 直连模式（测试服务器）：{hostname}:6086
  return `${protocol}://${host}:${PROD_PORTS.DIGITAL_HUMAN}`;
};

/**
 * 生成 gin-vue-admin API URL
 * 开发环境：http://62.234.7.27:8889（直连）
 * 生产环境(Nginx)：走同域反代 /gva-api
 * 生产环境(直连)：http://{hostname}:8888
 */
const buildGvaApiUrl = () => {
  const protocol = getHttpProtocol();
  const host = getHost();

  if (ENV_CONFIG.IS_PRODUCTION && ENV_CONFIG.USE_NGINX_PROXY) {
    return `${protocol}://${host}${NGINX_PATHS.GVA_API}`;
  }

  if (ENV_CONFIG.IS_PRODUCTION) {
    // 直连模式：gva-server 在当前服务器的 8888 端口
    return `${protocol}://${host}:${DEV_PORTS.GVA_STATIC}`;
  }

  // 开发环境：使用直连地址
  return DEV_SERVERS.GVA_API;
};

/**
 * 生成FunASR WebSocket URL
 * HTTPS环境必须通过Nginx代理（FunASR不支持WSS）
 */
const buildFunasrWsUrl = () => {
  // HTTPS环境：必须通过Nginx代理
  if (ENV_CONFIG.USE_HTTPS && ENV_CONFIG.USE_NGINX_PROXY) {
    return `${NGINX_PATHS.ASR}/`; // 相对路径，由FunASRClient转换为完整URL
  }

  // HTTP环境：直接连接
  return DEV_SERVERS.FUNASR_DIRECT;
};

// ========== 导出的URL常量（供各组件使用）==========

// EduGate主API（认证、课程、作业、通知等）
export const EDUGATE_API_URL = buildEdugateApiUrl();

// 高教社 HEP Authing SSO 配置
export const HEP_AUTHING_APP_ID = '69d9c1f410109fc81903c57a';
export const HEP_AUTHING_ORIGIN = 'https://ai-bupt.u2.hep.com.cn';
export const HEP_AUTHING_REDIRECT_URI = `${buildEdugateApiUrl()}/api/authing-callback`;
export const HEP_AUTHING_LOGOUT_URL = `https://ai-bupt.u2.hep.com.cn/login/profile/logout?redirect_uri=https://baigongai.cn`;

// GoMAown训练后端
export const GOMAOWN_API_URL = buildGomaownApiUrl();
export const GOMAOWN_WS_URL = buildGomaownWsUrl();

// CCMA先进综合训练后端
export const CCMA_WS_URL = buildCcmaWsUrl();

// CCMA HTTP API URL（cc-devices 等接口，与 WS 同端口）
const buildCcmaApiUrl = () => {
  const protocol = getHttpProtocol();
  const host = getHost();

  if (!ENV_CONFIG.IS_PRODUCTION) {
    return `${protocol}://${host}:${DEV_PORTS.CCMA}`;
  }

  if (ENV_CONFIG.USE_NGINX_PROXY) {
    // Nginx 反代时通过 /api/ccma 前缀转发到 CCMA，避免落到站点根路径
    return `${protocol}://${host}${NGINX_PATHS.CCMA_API}`;
  }

  return `${protocol}://${host}:${PROD_PORTS.CCMA}`;
};
export const CCMA_API_URL = buildCcmaApiUrl();

// AI秘书WebSocket
export const AI_SECRETARY_WS_URL = buildAiSecretaryWsUrl();

// 访客认证服务
export const VISITOR_AUTH_URL = buildVisitorAuthUrl();

// gin-vue-admin静态文件服务
export const GVA_STATIC_URL = buildGvaStaticUrl();

// gin-vue-admin管理后台前端
export const GVA_FRONTEND_URL = buildGvaFrontendUrl();

// AI绘图服务
export const AIDRAWIO_URL = buildAidrawioUrl();

// DiscussHub 研讨厅 WebSocket
export const DISCUSS_HUB_WS_URL = buildDiscussHubWsUrl();
// DiscussHub 研讨厅 HTTP API
export const DISCUSS_HUB_HTTP_URL = buildDiscussHubHttpUrl();

// GroupChatHub 群聊协作 WebSocket
export const GROUP_CHAT_HUB_WS_URL = buildGroupChatHubWsUrl();
// GroupChatHub 群聊协作 HTTP API
export const GROUP_CHAT_HUB_HTTP_URL = buildGroupChatHubHttpUrl();

// Dify智能体基础URL
export const DIFY_BASE_URL = buildDifyBaseUrl();

// 专家数字人服务（文字对话，ExpertService 8090）
export const EXPERT_SERVICE_URL = buildExpertServiceUrl();

// 音视频数字人服务（TTS/渲染，测试服务器 6086）
export const DIGITAL_HUMAN_URL = buildDigitalHumanUrl();

// Gin-Vue-Admin API（根据环境自动切换直连/反代）
export const GVA_API_URL = buildGvaApiUrl();

// FunASR语音识别服务配置
export const FUNASR_CONFIG = {
  wsUrl: buildFunasrWsUrl(),
  sampleRate: 16000, // 采样率 16kHz
  blockMs: 100,      // 每个音频分片时长（毫秒）
  itn: true,         // 启用逆文本正则化（数字转汉字等）
};

// 其他配置
export const LOAD_TIMEOUT = 5000; // 加载超时时间（毫秒）

// ========== 辅助函数（供组件使用）==========

/**
 * 动态生成智能体iframe URL
 * 支持两种输入格式：
 * 1. 完整URL: 'http://49.233.10.4/chatbot/xxx' → 提取路径后重建
 * 2. 路径: '/chatbot/xxx' → 直接拼接
 * @param {string} urlOrPath - 完整URL或路径
 * @returns {string} 完整的iframe URL
 */
export function getAgentIframeUrl(urlOrPath) {
  // 如果是完整URL，提取路径部分
  if (urlOrPath.startsWith('http://') || urlOrPath.startsWith('https://')) {
    try {
      const url = new URL(urlOrPath);
      return `${DIFY_BASE_URL}${url.pathname}`;
    } catch (e) {
      // URL解析失败，返回原值
      return urlOrPath;
    }
  }
  // 如果是路径，直接拼接
  return `${DIFY_BASE_URL}${urlOrPath}`;
}

/**
 * 动态生成FunASR WebSocket完整URL
 * 用于HTTPS环境下将相对路径转换为完整WSS URL
 * @returns {string} 完整的WebSocket URL
 */
export function getFunASRWebSocketUrl() {
  const wsUrl = FUNASR_CONFIG.wsUrl;

  // 如果是相对路径（HTTPS环境），转换为完整URL
  if (wsUrl.startsWith('/')) {
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${wsProtocol}//${window.location.host}${wsUrl}`;
  }

  // 如果已经是完整URL，直接返回
  return wsUrl;
}

/**
 * 获取当前环境信息（用于调试）
 */
export function getEnvironmentInfo() {
  return {
    isProduction: ENV_CONFIG.IS_PRODUCTION,
    useHttps: ENV_CONFIG.USE_HTTPS,
    useNginxProxy: ENV_CONFIG.USE_NGINX_PROXY,
    agentUseNginx: ENV_CONFIG.AGENT_USE_NGINX,
    edugateApiUrl: EDUGATE_API_URL,
    gomaownWsUrl: GOMAOWN_WS_URL,
    difyBaseUrl: DIFY_BASE_URL,
    funasrWsUrl: FUNASR_CONFIG.wsUrl,
  };
}
